import { Component, computed, forwardRef, inject, input, model, OnDestroy, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonIcon } from "@ionic/angular/standalone";
import { ToastService } from 'src/app/services/toast.service';
import { LoggingService } from 'src/app/services/logging.service';
import { DisplayMedia } from 'src/app/models/image';
import { CommonModule } from '@angular/common';
import { FileService } from 'src/app/services/file.service';
import { AudioPlayerComponent } from '../../audio-player/audio-player.component';

@Component({
  selector: 'app-audio-picker-edit',
  templateUrl: './audio-picker-edit.component.html',
  styleUrls: ['./audio-picker-edit.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AudioPickerEditComponent),
      multi: true,
    }
  ],
  standalone: true,
  imports: [IonIcon, CommonModule, FormsModule, ReactiveFormsModule, AudioPlayerComponent]
})
export class AudioPickerEditComponent implements ControlValueAccessor, OnDestroy {
  toastService = inject(ToastService);
  loggingService = inject(LoggingService);
  fileService = inject(FileService);

  id = input.required<string>();
  maxFileUploadSizeMb = 15;
  maxAudioDurationSeconds = input<number>(300); // 5 minutes default
  maxAudioAmount = input<number>();

  value = model<DisplayMedia | null>(null);
  private onChange: (value: DisplayMedia | null) => void = () => { };
  private onTouched: () => void = () => { };

  objectUrl: string | undefined = undefined;

  supportedMimeTypes = computed(() => {
    const mediaType = 'audio';
    const supportedFileTypes = this.fileService.supportedFileFormats()[mediaType].map(t => t.mimeSubType);
    let unique = Array.from(new Set(supportedFileTypes));
    return unique.map(t => `${mediaType}/${t}`);
  });
  supportedMimeTypesString = computed<string>(() => {
    return this.supportedMimeTypes().join(',');
  });

  writeValue(value: DisplayMedia | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: DisplayMedia | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  ngOnDestroy() {
    if (this.objectUrl)
      URL.revokeObjectURL(this.objectUrl);
  }

  private getAudioDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.src = URL.createObjectURL(file);

      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(audio.src);
        resolve(audio.duration);
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audio.src);
        reject(new Error('Could not determine audio duration'));
      };
    });
  }

  async addAudio(event: Event) {
    const element = event.target as HTMLInputElement;
    const fileList = element.files;
    if (!fileList?.length) return;

    const file = fileList[0] as File;

    try {
      const isSupportedFileType = this.supportedMimeTypes().some(type => type === file.type);
      if (!isSupportedFileType) {
        const supportedExtensions = this.fileService.supportedFileFormats().audio
          .flatMap(f => f.extension)
          .join(', ');
        throw new Error(`Only ${supportedExtensions} audio files are supported!`);
      }

      // Check file size
      const fileSizeMb = file.size / (1024 * 1024);
      if (fileSizeMb > this.maxFileUploadSizeMb) {
        throw new Error(`File is too large. Max ${this.maxFileUploadSizeMb}Mb allowed`);
      }

      // Check duration
      const duration = await this.getAudioDuration(file);
      if (duration > this.maxAudioDurationSeconds()) {
        throw new Error(`Audio duration exceeds ${this.maxAudioDurationSeconds() / 60} minutes limit`);
      }

      const src = URL.createObjectURL(file);
      this.objectUrl = src;

      const newAudio: DisplayMedia = {
        originalFile: file,
        src,
        status: 'new',
        duration: duration
      };

      this.value.set(newAudio);
      this.onChange(newAudio);
      this.onTouched();

    } catch (e) {
      this.loggingService.error(e);
      const error = e as Error;
      this.toastService.error(error.message);
    }

    element.value = ''; // Reset input
  }

  deleteAudio() {
    const value = this.value();
    const src = this.value()?.src;
    if (src) {
      URL.revokeObjectURL(src);
    }
    if (value?.fileReferenceId) {
      value.status = 'deleted';
    }
    this.value.set(null);
    this.onChange(null);
    this.onTouched();
  }

  formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  }
}